import { useState, useEffect, useCallback, useRef } from "react";
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, AreaChart, Area,
  XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, CartesianGrid, ReferenceLine,
} from "recharts";
import { apiGet, apiPost } from "../api";
import usePageTitle from "../hooks/usePageTitle";
import {
  ChoroplethMap, crossoverLabel, STATUS_COLORS, SUPPLIER_COLORS, tooltipStyle, TT_ITEM, TT_LABEL,
  PINK, PURPLE, CYAN, GREEN, AMBER, RED, MUTE,
} from "./SupplyScanner";

// Full-bleed TV / kiosk view of the Supply Scanner overview — everything on
// one 1920×1080 screen, no scrolling, data auto-refreshes hourly.
const REFRESH_MS = 60 * 60 * 1000;
const AXIS = { fontSize: 10, fill: MUTE };
const LEGEND = { fontSize: 10.5 };

const gbp0 = (n) => "£" + Math.round(n || 0).toLocaleString("en-GB");
const gbp2 = (n) => "£" + Number(n || 0).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const gbpTick = (v) => (v >= 1000 ? `£${(v / 1000).toLocaleString("en-GB", { maximumFractionDigits: v >= 10000 ? 0 : 1 })}k` : `£${v}`);

function TvPanel({ title, subtitle, children, style }) {
  return (
    <div style={{ background: "var(--color-bg-hemocyanin)", border: "1px solid var(--hairline)", borderRadius: 14,
      padding: "10px 14px 8px", display: "flex", flexDirection: "column", minHeight: 0, ...style }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 4, flexShrink: 0 }}>
        <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: ".07em", textTransform: "uppercase", color: "var(--color-text-4)", whiteSpace: "nowrap" }}>{title}</span>
        {subtitle && <span style={{ fontSize: 10.5, color: MUTE, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{subtitle}</span>}
      </div>
      <div style={{ flex: 1, minHeight: 0 }}>{children}</div>
    </div>
  );
}

// Full-screen canvas fireworks — pointer-events none, sits above everything.
// Rockets launch from below-screen, arc up, and explode into one of several
// burst types (peony, willow, chrysanthemum, ring, palm). Uses additive
// blending and pre-rendered glow sprites for a fast, glowing look.
function FireworksOverlay() {
  const ref = useRef(null);
  useEffect(() => {
    const canvas = ref.current;
    const ctx = canvas.getContext("2d", { alpha: true });
    let raf, running = true;
    // Keep the canvas at CSS pixel size (DPR=1). Additive blending hides the
    // difference vs a 2×-scaled canvas but halves the fill area cost.
    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener("resize", resize);

    // Palettes: [core, mid, halo].
    const palettes = [
      ["#ff5a8a", "#ff9dc5", "#ffe4ef"],
      ["#a855f7", "#c4b5fd", "#efe7ff"],
      ["#22d3ee", "#a5f3fc", "#e6feff"],
      ["#34d399", "#a7f3d0", "#ecfdf5"],
      ["#fbbf24", "#fde68a", "#fffbeb"],
      ["#f87171", "#fecaca", "#fff1f2"],
      ["#60a5fa", "#bfdbfe", "#eff6ff"],
    ];
    const types = ["peony", "willow", "chrys", "ring", "palm"];

    // Pre-render one soft radial-gradient sprite per color — drawImage() is
    // dramatically faster than arc()+fill() when we've got hundreds of
    // particles a frame, and the gradient already contains the halo so we
    // skip the second draw call per particle.
    const SPR = 36;
    const makeSprite = (color) => {
      const c = document.createElement("canvas");
      c.width = c.height = SPR;
      const g = c.getContext("2d");
      const cx = SPR / 2;
      const grd = g.createRadialGradient(cx, cx, 0, cx, cx, cx);
      grd.addColorStop(0.0, "#ffffff");
      grd.addColorStop(0.2, color);
      grd.addColorStop(0.55, color + "80");
      grd.addColorStop(1.0, "#00000000");
      g.fillStyle = grd;
      g.fillRect(0, 0, SPR, SPR);
      return c;
    };
    const spriteCache = new Map();
    const getSprite = (color) => {
      let s = spriteCache.get(color);
      if (!s) { s = makeSprite(color); spriteCache.set(color, s); }
      return s;
    };
    const emberSprite = makeSprite("#ffd88a");
    // Pre-warm palette sprites so first burst doesn't allocate mid-frame.
    palettes.forEach(([a, b, c]) => { getSprite(a); getSprite(b); getSprite(c); });

    const rockets = [];
    const parts = [];
    const sparks = [];
    let flash = 0;
    const MAX_PARTS = 900;
    const MAX_SPARKS = 500;

    function launchRocket() {
      const x = canvas.width * (0.1 + Math.random() * 0.8);
      const y = canvas.height * (0.95 + Math.random() * 0.06);
      const targetY = canvas.height * (0.12 + Math.random() * 0.35);
      const palette = palettes[(Math.random() * palettes.length) | 0];
      const type = types[(Math.random() * types.length) | 0];
      const g = 0.045;
      const vy = -Math.sqrt(2 * g * (y - targetY));
      rockets.push({
        x, y,
        vx: (Math.random() - 0.5) * 0.6,
        vy, g,
        targetY, palette, type,
      });
    }

    function explode(rocket) {
      const { x, y, palette, type } = rocket;
      flash = Math.min(1, flash + 0.35);
      const core = palette[0], mid = palette[1];

      let n;
      if (type === "ring") n = 72;
      else if (type === "willow") n = 90;
      else if (type === "palm") n = 24;
      else n = 95 + ((Math.random() * 25) | 0);

      for (let i = 0; i < n; i++) {
        const a = (Math.PI * 2 * i) / n + Math.random() * 0.03;
        let sp;
        if (type === "ring") sp = 4.2 + Math.random() * 0.3;
        else if (type === "willow") sp = 1.1 + Math.random() * 1.5;
        else if (type === "palm") sp = 2 + Math.random() * 4.2;
        else if (type === "chrys") sp = 1.2 + Math.pow(Math.random(), 0.7) * 4.6;
        else sp = 1.4 + Math.pow(Math.random(), 0.55) * 4.6;

        const upwardBias = type === "palm" ? 1.6 : 0;
        parts.push({
          x, y,
          vx: Math.cos(a) * sp,
          vy: Math.sin(a) * sp - upwardBias,
          life: 1,
          decay: type === "willow" ? 0.006 + Math.random() * 0.002
               : type === "chrys" ? 0.0095 + Math.random() * 0.004
               : 0.011 + Math.random() * 0.005,
          size: type === "willow" ? 9 : 11,
          color: Math.random() < 0.18 ? mid : core,
          twinkle: Math.random() < 0.35,
          drag: type === "willow" ? 0.988 : 0.977,
          gravity: type === "willow" ? 0.03
                 : type === "palm" ? 0.055
                 : 0.042,
          // Trails are the biggest perf sink — keep them modest and reserve
          // heavy trailing for willow where it's part of the look.
          trailChance: type === "willow" ? 0.35
                     : type === "chrys" ? 0.22
                     : type === "palm" ? 0.18
                     : 0.1,
        });
      }
      if (parts.length > MAX_PARTS) parts.splice(0, parts.length - MAX_PARTS);
    }

    let last = performance.now();
    let sinceLaunch = 0;
    let sinceCluster = 0;

    const step = (now) => {
      if (!running) return;
      const dt = Math.min(now - last, 33); last = now;
      sinceLaunch += dt;
      sinceCluster += dt;

      if (sinceLaunch > 320 + Math.random() * 260) {
        sinceLaunch = 0;
        launchRocket();
      }
      if (sinceCluster > 7000 + Math.random() * 5000) {
        sinceCluster = 0;
        const k = 3 + ((Math.random() * 2) | 0);
        for (let i = 0; i < k; i++) setTimeout(launchRocket, i * 130);
      }

      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.globalCompositeOperation = "lighter";

      if (flash > 0.01) {
        ctx.fillStyle = `rgba(255,255,255,${flash * 0.07})`;
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        flash *= 0.82;
      }

      // Rockets — cheap ember trail, one sprite blit per rocket
      for (let i = rockets.length - 1; i >= 0; i--) {
        const r = rockets[i];
        r.x += r.vx;
        r.y += r.vy;
        r.vy += r.g;
        // Trail every other frame to halve the ember count.
        if (Math.random() < 0.5) {
          sparks.push({ x: r.x, y: r.y + 2, life: 0.55, sprite: emberSprite, size: 6 });
        }
        ctx.globalAlpha = 1;
        ctx.drawImage(emberSprite, r.x - 4, r.y - 4, 8, 8);
        if (r.y <= r.targetY || r.vy >= 0) {
          explode(r);
          rockets.splice(i, 1);
        }
      }

      // Hard cap sparks
      if (sparks.length > MAX_SPARKS) sparks.splice(0, sparks.length - MAX_SPARKS);

      // Sparks — one drawImage each, no fillStyle changes
      for (let i = sparks.length - 1; i >= 0; i--) {
        const s = sparks[i];
        s.life -= 0.075;
        if (s.life <= 0) { sparks.splice(i, 1); continue; }
        ctx.globalAlpha = s.life;
        const sz = s.size * s.life * 1.5;
        ctx.drawImage(s.sprite, s.x - sz / 2, s.y - sz / 2, sz, sz);
      }

      // Particles — one drawImage per particle (sprite carries the glow)
      for (let i = parts.length - 1; i >= 0; i--) {
        const p = parts[i];
        p.x += p.vx;
        p.y += p.vy;
        p.vy += p.gravity;
        p.vx *= p.drag;
        p.vy *= p.drag;
        p.life -= p.decay;
        if (p.life <= 0) { parts.splice(i, 1); continue; }

        let alpha = p.life;
        if (p.twinkle && p.life < 0.4) alpha *= 0.4 + Math.random() * 0.6;
        ctx.globalAlpha = alpha;
        const sz = p.size * (0.55 + p.life * 0.8);
        const spr = getSprite(p.color);
        ctx.drawImage(spr, p.x - sz / 2, p.y - sz / 2, sz, sz);

        if (Math.random() < p.trailChance) {
          sparks.push({ x: p.x, y: p.y, life: 0.35 + Math.random() * 0.3, sprite: spr, size: p.size * 0.5 });
        }
      }
      ctx.globalAlpha = 1;
      ctx.globalCompositeOperation = "source-over";

      raf = requestAnimationFrame(step);
    };

    launchRocket();
    raf = requestAnimationFrame(step);

    return () => {
      running = false;
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
    };
  }, []);
  return <canvas ref={ref} data-testid="tv-fireworks" style={{ position: "fixed", inset: 0, pointerEvents: "none", zIndex: 60 }} />;
}

function MilestoneBanner({ milestone }) {
  return (
    <div data-testid="tv-milestone-banner" style={{ flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", gap: 12,
      borderRadius: 12, padding: "9px 18px",
      background: "linear-gradient(100deg, #a855f7, #ec4899 40%, #f59e0b 75%, #34d399)",
      backgroundSize: "300% 100%", animation: "tvBannerShift 6s ease infinite" }}>
      <style>{`@keyframes tvBannerShift { 0%{background-position:0% 50%} 50%{background-position:100% 50%} 100%{background-position:0% 50%} }`}</style>
      <span style={{ fontFamily: "var(--font-display)", fontSize: 19, fontWeight: 800, color: "#fff", letterSpacing: ".02em", textShadow: "0 1px 4px rgba(0,0,0,0.35)" }}>
        MILESTONE — OSS has overtaken LPO!{milestone.week_label ? ` More orders now go through OSS than LPO (since w/c ${milestone.week_label}).` : ""}
      </span>
      {milestone.source === "simulation" && (
        <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: ".08em", color: "#fff", border: "1.5px solid rgba(255,255,255,0.75)", borderRadius: 999, padding: "3px 10px" }}>
          SIMULATION
        </span>
      )}
    </div>
  );
}

function TvKpi({ label, value, color }) {
  return (
    <div style={{ background: "var(--color-bg-hemocyanin)", border: "1px solid var(--hairline)", borderRadius: 12,
      padding: "10px 14px", display: "flex", flexDirection: "column", minHeight: 0 }}>
      <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: ".07em", textTransform: "uppercase", color: "var(--color-text-4)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{label}</div>
      <div style={{ fontFamily: "var(--font-display)", fontSize: 31, fontWeight: 700, marginTop: "auto", paddingTop: 4, color: color || "var(--color-text-1)" }}>{value}</div>
    </div>
  );
}

export default function SupplyScannerTv() {
  usePageTitle("Supply Scanner — TV");
  const [stats, setStats] = useState(null);
  const [vsLpo, setVsLpo] = useState(null); // weekly OSS vs LPO series
  const [milestone, setMilestone] = useState(null); // crossover celebration state
  const [tick, setTick] = useState(0); // bumps hourly → remounts the map so it refetches
  const [now, setNow] = useState(new Date());

  const load = useCallback(async (pull) => {
    if (pull) {
      // Hourly: try to pull fresh orders first; fall back to cached stats.
      try {
        const res = await apiPost("/supply-scanner/refresh");
        if (res?.stats) { setStats(res.stats); setTick((t) => t + 1); }
      } catch { /* server creds may be missing — cached stats below */ }
    }
    try {
      const d = await apiGet("/supply-scanner/stats");
      if (d) { setStats(d); setTick((t) => t + 1); }
    } catch { /* keep showing the last good data */ }
    try {
      const v = await apiGet("/supply-scanner/oss-vs-lpo", { params: { granularity: "weekly" } });
      if (v) setVsLpo(v);
    } catch { /* keep last good series */ }
  }, []);

  useEffect(() => {
    load(false);
    const id = setInterval(() => load(true), REFRESH_MS);
    return () => clearInterval(id);
  }, [load]);

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 30 * 1000);
    return () => clearInterval(id);
  }, []);

  // Celebration poll — every minute so the banner/fireworks appear promptly
  // when the crossover lands (or a simulation is started from Settings).
  useEffect(() => {
    let cancelled = false;
    const check = async () => {
      try {
        const m = await apiGet("/supply-scanner/milestone");
        if (!cancelled && m) setMilestone(m);
      } catch { /* keep last state */ }
    };
    check();
    const id = setInterval(check, 60 * 1000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  const root = { position: "fixed", inset: 0, zIndex: 50, background: "var(--color-bg-siphon)", overflow: "hidden",
    display: "flex", justifyContent: "center" };
  // Cap the content width + centre it — on a wide monitor this stops charts
  // stretching edge-to-edge (the main cause of the "elongated" look).
  const content = { width: "100%", maxWidth: 2200, height: "100%", display: "flex", flexDirection: "column",
    padding: "20px 32px 26px", gap: 18, boxSizing: "border-box" };

  if (!stats) {
    return <div data-testid="supply-scanner-tv" style={{ ...root, alignItems: "center", justifyContent: "center", color: MUTE, fontSize: 16 }}>Loading Supply Scanner…</div>;
  }

  const c = stats.cards || {};
  const s = stats;
  const kpis = [
    { label: "Total orders", value: (c.totalOrders || 0).toLocaleString() },
    { label: "Total spend", value: gbp0(c.spend), color: GREEN },
    { label: "Avg order value", value: gbp2(c.avgOrderValue), color: CYAN },
    { label: "This week", value: c.ordersThisWeek, color: CYAN },
    { label: "Ordering engineers", value: c.activeUsers, color: PURPLE },
    { label: "Onboarded (WorkOS)", value: c.onboardedEngineers ?? c.knownLoggedInUsers, color: PINK },
    { label: "Adoption", value: `${c.adoptionRate ?? Math.round((c.activeUsers / (c.onboardedEngineers || c.knownLoggedInUsers || 1)) * 100)}%`, color: AMBER },
    { label: "Success rate", value: `${c.successRate}%`, color: c.successRate >= 75 ? GREEN : c.successRate >= 60 ? AMBER : RED },
  ];
  const supplierBySpend = [...(s.supplierChart || [])].sort((a, b) => (b.spend || 0) - (a.spend || 0));
  const statusData = Object.entries(s.statusCounts || {}).filter(([, v]) => v > 0).map(([name, value]) => ({ name, value }));

  return (
    <div data-testid="supply-scanner-tv" style={root}>
      {milestone?.fireworks_active && <FireworksOverlay />}
      <div style={content}>
      {milestone?.banner_active && <MilestoneBanner milestone={milestone} />}
      {/* header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <span style={{ width: 24, height: 24, borderRadius: 999, background: "radial-gradient(circle at 35% 35%, #ff7ab8, #ec4899 60%, #b81f6e)" }} />
          <span style={{ fontFamily: "var(--font-display)", fontSize: 24, fontWeight: 700, color: "var(--color-text-1)" }}>Supply Scanner</span>
          <span style={{ fontSize: 13, color: MUTE }}>All time · since first OSS order</span>
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={{ fontFamily: "var(--font-display)", fontSize: 24, fontWeight: 700, color: "var(--color-text-1)", lineHeight: 1.1 }} data-testid="tv-clock">
            {now.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}
          </div>
          <div style={{ fontSize: 11.5, color: MUTE }}>
            {s.lastUpdated ? `Updated ${new Date(s.lastUpdated).toLocaleString("en-GB", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}` : ""} · refreshes hourly
          </div>
        </div>
      </div>

      {/* KPI strip */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(8, 1fr)", gap: 10, flexShrink: 0 }} data-testid="tv-kpis">
        {kpis.map((k) => <TvKpi key={k.label} {...k} />)}
      </div>

      {/* chart grid — map spans both rows on the right */}
      <div style={{ flex: 1, minHeight: 0, display: "grid", gridTemplateColumns: "1fr 1fr 1fr 0.82fr", gridTemplateRows: "1fr 1fr", gap: 18 }}>
        <TvPanel title="Orders by supplier">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={s.supplierChart} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
              <CartesianGrid stroke="#241a3d" vertical={false} />
              <XAxis dataKey="label" tick={AXIS} interval={0} angle={-18} textAnchor="end" height={42} />
              <YAxis tick={AXIS} />
              <Tooltip contentStyle={tooltipStyle} itemStyle={TT_ITEM} labelStyle={TT_LABEL} cursor={{ fill: "#ffffff08" }} />
              <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                {s.supplierChart.map((_, i) => <Cell key={i} fill={SUPPLIER_COLORS[i % SUPPLIER_COLORS.length]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </TvPanel>

        <TvPanel title="Orders over time" subtitle="monthly + running total">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={s.trend} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
              <CartesianGrid stroke="#241a3d" vertical={false} />
              <XAxis dataKey="label" tick={AXIS} />
              <YAxis tick={AXIS} />
              <Tooltip contentStyle={tooltipStyle} itemStyle={TT_ITEM} labelStyle={TT_LABEL} />
              <Legend wrapperStyle={LEGEND} />
              <Line type="monotone" dataKey="monthly" name="Monthly" stroke={CYAN} strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="cumulative" name="Cumulative" stroke={PINK} strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </TvPanel>

        <TvPanel title="Order status">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={statusData} dataKey="value" nameKey="name" innerRadius="52%" outerRadius="82%" paddingAngle={2}>
                {statusData.map((d, i) => <Cell key={i} fill={STATUS_COLORS[d.name] || MUTE} />)}
              </Pie>
              <Tooltip contentStyle={tooltipStyle} itemStyle={TT_ITEM} labelStyle={TT_LABEL} />
              <Legend wrapperStyle={LEGEND} />
            </PieChart>
          </ResponsiveContainer>
        </TvPanel>

        <TvPanel title="Order geography" subtitle="orders by region (DNO licence areas)" style={{ gridColumn: 4, gridRow: "1 / span 2" }}>
          <ChoroplethMap key={tick} level="region" height="100%" />
        </TvPanel>

        <TvPanel title="Spend over time" subtitle="successful orders">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={s.trend} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid stroke="#241a3d" vertical={false} />
              <XAxis dataKey="label" tick={AXIS} />
              <YAxis tick={AXIS} tickFormatter={gbpTick} />
              <Tooltip contentStyle={tooltipStyle} itemStyle={TT_ITEM} labelStyle={TT_LABEL} formatter={(v) => gbp2(v)} />
              <Legend wrapperStyle={LEGEND} />
              <Line type="monotone" dataKey="spend" name="Monthly" stroke={GREEN} strokeWidth={2} dot={s.trend.length <= 2} />
              <Line type="monotone" dataKey="cumulativeSpend" name="Cumulative" stroke={PINK} strokeWidth={2} dot={s.trend.length <= 2} />
            </LineChart>
          </ResponsiveContainer>
        </TvPanel>

        <TvPanel title="Spend by supplier" subtitle="successful orders">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={supplierBySpend} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid stroke="#241a3d" vertical={false} />
              <XAxis dataKey="label" tick={AXIS} interval={0} angle={-18} textAnchor="end" height={52} />
              <YAxis tick={AXIS} tickFormatter={gbpTick} />
              <Tooltip contentStyle={tooltipStyle} itemStyle={TT_ITEM} labelStyle={TT_LABEL} cursor={{ fill: "#ffffff08" }} formatter={(v) => gbp2(v)} />
              <Bar dataKey="spend" name="Spend" radius={[4, 4, 0, 0]}>
                {supplierBySpend.map((_, i) => <Cell key={i} fill={SUPPLIER_COLORS[i % SUPPLIER_COLORS.length]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </TvPanel>

        <TvPanel title="OSS vs LPO" subtitle="orders per week on each system">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={vsLpo?.series || []} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
              <defs>
                <linearGradient id="tvOssFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={PURPLE} stopOpacity={0.4} />
                  <stop offset="100%" stopColor={PURPLE} stopOpacity={0.04} />
                </linearGradient>
                <linearGradient id="tvLpoFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={AMBER} stopOpacity={0.35} />
                  <stop offset="100%" stopColor={AMBER} stopOpacity={0.04} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="#241a3d" vertical={false} />
              <XAxis dataKey="label" tick={AXIS} minTickGap={44} />
              <YAxis tick={AXIS} />
              <Tooltip contentStyle={tooltipStyle} itemStyle={TT_ITEM} labelStyle={TT_LABEL}
                labelFormatter={(l) => `Week commencing ${l}`} />
              <Legend wrapperStyle={LEGEND} />
              {crossoverLabel(vsLpo?.series) && (
                <ReferenceLine x={crossoverLabel(vsLpo?.series)} stroke={GREEN} strokeDasharray="5 4" strokeWidth={1.5}
                  label={{ value: "OSS overtakes LPO", position: "left", angle: -90, fill: GREEN, fontSize: 10, fontWeight: 700, offset: 10 }} />
              )}
              <Area type="monotone" dataKey="oss" name="OSS" stroke={PURPLE} strokeWidth={2} fill="url(#tvOssFill)" dot={false} />
              <Area type="monotone" dataKey="lpo" name="LPO" stroke={AMBER} strokeWidth={2} fill="url(#tvLpoFill)" dot={false} />
            </AreaChart>
          </ResponsiveContainer>
        </TvPanel>
      </div>
      </div>
    </div>
  );
}
